import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Breadcrumbs — accessible visual breadcrumb trail + BreadcrumbList JSON-LD.
 *
 * Usage:
 *   <Breadcrumbs trail={[
 *     { name: "Home", href: "/" },
 *     { name: "Resources", href: "/blog" },
 *     { name: "MSP vs VMS" },                  // last item: no href = current page
 *   ]} />
 *
 * Renders both:
 *  1. A semantic <nav aria-label="Breadcrumb"> with chevron separators
 *  2. A JSON-LD BreadcrumbList script tag for SEO
 */
const SITE_ROOT = "https://www.tritalcare.com";

export default function Breadcrumbs({ trail = [], className = "" }) {
  if (!trail || trail.length === 0) return null;

  const ld = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.name,
      ...(t.href ? { item: `${SITE_ROOT}${t.href}` } : {}),
    })),
  };

  return (
    <>
      <nav
        aria-label="Breadcrumb"
        className={`flex items-center text-xs text-slate-500 ${className}`}
        data-testid="breadcrumbs"
      >
        <ol className="flex items-center flex-wrap gap-y-1">
          {trail.map((t, i) => {
            const isLast = i === trail.length - 1;
            return (
              <li key={i} className="flex items-center">
                {i > 0 && (
                  <ChevronRight className="w-3 h-3 mx-2 text-slate-300" aria-hidden="true" />
                )}
                {isLast || !t.href ? (
                  <span
                    aria-current="page"
                    className="text-slate-700 font-medium"
                    data-testid={`breadcrumb-current`}
                  >
                    {t.name}
                  </span>
                ) : (
                  <Link
                    to={t.href}
                    className="hover:text-trital-navy transition-colors"
                    data-testid={`breadcrumb-link-${i}`}
                  >
                    {t.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
      />
    </>
  );
}
